import {
  useState,
  useLayoutEffect,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'

import authService from './utilities/services/auth.service'

import UserContext from './context/User/User'
import LoadingContext from './context/Loading/Loading'

import { ModalProvider } from './providers/ModalProvider/ModalProvider'
import { DepositProvider } from './providers/DepositProvider/DepositProvider'
import { CreatePackageProvider } from './providers/CreatePackageProvider/CreatePackageProvider'
import { CreateSeasonProvider } from './providers/CreateSeasonProvider/CreateSeasonProvider'

import { getOrganizationPermissions } from './utilities/api'
import { toggleContainer, changeBackground } from './utilities/helpers'

import { Navigation } from './components'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'

import Router from './Router'
import BulkReservedImportToastHost from './components/BulkReservedImportToastHost/BulkReservedImportToastHost'
import SupportWidget from './components/SupportWidget'
import WhatsNewProvider from './components/WhatsNew'
import useReportAccessTracking from './hooks/useReportAccessTracking'

function App() {
  let location = useLocation()
  useReportAccessTracking()

  const user = authService.getUser()
  const me = useContext(UserContext)
  const token = authService.token()
  const decoded = token ? jwtDecode(token) : ''
  const navigate = useNavigate()

  const [authenticated, setAuthenticated] = useState(false)

  const [isLoading, setIsLoading] = useState(true) // default to true

  const [orgPermissions, setOrgPermissions] = useState([])

  const [org, setOrganization] = useState('')

  const [show, setShow] = useState(false)

  const handleClose = () => {
    authService.logoutUser()
    setShow(false)
    navigate('/login')
  }

  useLayoutEffect(() => {
    changeBackground(location.pathname)
    toggleContainer(location.pathname)
  }, [location.pathname])

  // get org permissions once on initial load
  useEffect(() => {
    let permissions = []
    getOrganizationPermissions().then((res) => {
      Object.values(res.data.data).map((obj) =>
        permissions.push({ id: obj?.id, name: obj?.attributes?.name })
      )
      setOrgPermissions(permissions)
    })
  }, [])

  useEffect(() => {
    if (!user) return

    if (decoded) {
      if (Date.now() >= decoded.exp * 1000) {
        setShow(true)
      }
      return
    }
  }, [location, show])

  // useCallback ensures showLoading and hideLoading aren't recreated on every render
  const showLoading = useCallback(() => setIsLoading(true), [])
  const hideLoading = useCallback(() => setIsLoading(false), [])

  // useMemo prevents value from being recreated unless isLoading, showLoading, or hideLoading change
  // Memoized value to avoid unnecessary re-renders
  const value = useMemo(
    () => ({ isLoading, showLoading, hideLoading }),
    [isLoading, showLoading, hideLoading]
  )

  return (
    <>
      <UserContext.Provider
        value={{
          authenticated,
          setAuthenticated,
          user,
          setOrganization,
          orgPermissions,
        }}
      >
        <ModalProvider>
          <DepositProvider>
            <CreatePackageProvider>
              <CreateSeasonProvider>
                <WhatsNewProvider>
                  <Navigation user={user} me={me} />
                  <div className="container" id="main-container">
                    <LoadingContext.Provider value={value}>
                      <Router />
                    </LoadingContext.Provider>
                  </div>
                </WhatsNewProvider>
              </CreateSeasonProvider>
            </CreatePackageProvider>
          </DepositProvider>
        </ModalProvider>
      </UserContext.Provider>

      <Modal show={show} onHide={handleClose} backdrop={true}>
        <Modal.Header closeButton>
          <Modal.Title>Expired Session</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Your session has expired. You will need to login to contine
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleClose}>
            Login
          </Button>
        </Modal.Footer>
      </Modal>

      <BulkReservedImportToastHost />
      <SupportWidget />
    </>
  )
}

export default App
